<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { ChevronDown, Check, X } from "utruckin-icons";
import { IDropdown, IDropdownOptions } from "@/types/main";

const props = withDefaults(
  defineProps<IDropdownOptions & { fullWidth?: boolean }>(),
  {
    position: "bottom",
    label: "",
    items: () => [],
    modelValue: "",
    disabled: false,
    fullWidth: false,
    showBadge: false,
    clearable: false,
    dropdownFullWidth: false,
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", v: number | string): void;
}>();

const isOpen = ref(false);
const rootRef = ref<HTMLElement | null>(null);

const listStyle = ref({
  top: "0px",
  left: "0px",
  width: "200px",
  transformOrigin: "top",
});

const selectedItem = computed(() => {
  return props.items.find((i) => i.value === props.modelValue) ?? null;
});

const selectedLabel = computed(() => selectedItem.value?.name || "");
const selectedColor = computed(() => selectedItem.value?.color || null);

const hasSelection = computed(() => selectedLabel.value.length > 0);

function updateListPosition() {
  if (!rootRef.value) return;
  const rect = rootRef.value.getBoundingClientRect();
  listStyle.value = {
    width: `${rect.width}px`,
    left: `${rect.left + window.scrollX}px`,
    top:
      props.position === "top"
        ? `${rect.top + window.scrollY - 6}px`
        : `${rect.bottom + window.scrollY + 6}px`,
    transformOrigin: props.position === "top" ? "bottom" : "top",
  };
}

function toggle() {
  if (props.disabled) return;
  if (!isOpen.value) {
    updateListPosition();
  }
  isOpen.value = !isOpen.value;
}

function choose(item: IDropdown) {
  emit("update:modelValue", item.value);
  isOpen.value = false;
}

function onDocumentClick(e: MouseEvent) {
  const el = rootRef.value;
  if (!el) return;
  const target = e.target as Node | null;
  if (!target) return;
  if (!el.contains(target)) {
    isOpen.value = false;
  }
}

onMounted(() => {
  document.addEventListener("click", onDocumentClick);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", onDocumentClick);
});

const clearValue = () => {
  emit("update:modelValue", null);
};
</script>

<template>
  <div
    ref="rootRef"
    class="c-co-dropdown"
    :class="{
      active: isOpen,
      disabled: disabled,
      'full-width': fullWidth,
    }"
    @click="toggle"
  >
    <div class="value" :class="{ 'is-placeholder': !hasSelection }">
      <span
        v-if="hasSelection && selectedColor"
        class="color-dot"
        :style="{ backgroundColor: `var(${selectedColor})` }"
      />
      {{ selectedLabel || label }}
    </div>

    <div class="actions">
      <div v-if="clearable && modelValue" class="clear" @click="clearValue">
        <X :size="20" />
      </div>

      <div class="icon" :class="{ rotate: isOpen }">
        <ChevronDown :size="20" />
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="isOpen"
        class="c-co-dropdown__list"
        :style="{
          position: 'fixed',
          top: listStyle.top,
          left: listStyle.left,
          width: listStyle.width,
          transform: position === 'top' ? 'translateY(-100%)' : 'none',
        }"
      >
        <div
          v-for="(item, index) in items"
          :key="item.value || index"
          class="c-co-dropdown__item"
          @click.stop="choose(item)"
        >
          <span
            v-if="item.color"
            class="color-dot"
            :style="{ backgroundColor: `var(${item.color})` }"
          />
          <span class="item-text">{{ item.name }}</span>
          <Check v-if="item.value === modelValue" class="check" />
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
.c-co-dropdown {
  width: 200px;

  &.full-width {
    width: 100%;
  }

  height: 44px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: 1px solid var(--color-border);
  border-radius: 14px;
  padding: 10px 12px;
  cursor: pointer;
  position: relative;
  background: var(--color-secondary-system-background);
  font-size: 16px;
  line-height: 24px;
  font-weight: 500;
  color: var(--color-label);

  &:hover {
    border-color: var(--color-primary-disabled);
  }

  &.disabled {
    pointer-events: none;
    opacity: 0.6;
  }

  .value {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: var(--color-label);
    background: transparent;
    display: flex;
    align-items: center;
    gap: 6px;

    &.is-placeholder {
      color: var(--color-label-secondary);
    }
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 4px;

    .clear {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 20px;
      height: 20px;
      color: var(--color-label-secondary);
      cursor: pointer;
    }

    .icon {
      display: flex;
      align-items: center;
      transition: 0.2s ease;
      background: transparent;

      &.rotate {
        transform: rotate(180deg);
      }
    }
  }
}
</style>

<style lang="scss">
.c-co-dropdown__list {
  z-index: 99999;
  max-height: 280px;
  background: var(--color-system-background);
  border: 1px solid var(--color-border);
  border-radius: 14px;
  overflow: hidden;
  overflow-y: scroll;
  scrollbar-width: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  padding: 8px;
  background: var(--color-secondary-system-background);
}

.c-co-dropdown__item {
  height: 32px;
  padding: 8px;
  color: var(--color-label);
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  flex-wrap: nowrap !important;
  border-radius: 8px;

  &:hover {
    background: var(--color-system-background);

    .item-text {
      background: transparent;
    }
  }

  .color-dot {
    width: 16px;
    min-width: 16px;
    height: 16px;
    border-radius: 50%;
  }

  .check {
    width: 16px;
    color: var(--color-label-secondary);
    font-weight: 700;
  }
}
</style>
